import PhoneNumber from 'awesome-phonenumber';
import flatten from 'flat';
import { cloneDeep, get, isEqual, replace, set } from 'lodash';
import moment from 'moment';
import Validator from 'validatorjs';
import { filterXSS } from 'xss';

import { i18n, t } from '../utils/translations';

const INTERNAL_CURRENT_DATE = 'internalCurrentAddressDate';
const INTERNAL_PAST_DATES = 'internalPastAddressesDates';
export const CaseValidationActions = {
  setCustomValidators() {
    const { actions, sdkActions } = this.state;

    Validator.register('address', (value, requirement, attribute) => {
      return value.line1 && value.country;
    });

    Validator.register('address-us', (value) => {
      return !!value.country;
    });

    Validator.register('xss', (value, requirement, attribute) => {
      if (typeof value === 'boolean' || typeof value === 'number' || typeof value === 'undefined' || value === null)
        return true;

      return value.toString() === filterXSS(value);
    });

    Validator.register('phone', (value, requirement, attribute) => {
      value = replace(value, '+', '');
      const pn = new PhoneNumber(value, requirement.toUpperCase());
      return pn.isValid();
    });

    Validator.register('mobile', (value, requirement, attribute) => {
      return ['OK', 'TOO_MANY_FAIL_ATTEMPTS', 'SUCCESS'].includes(get(value, 'verificationStatus')) && value.phone;
    });

    Validator.register('disallowFuture', (value, requirement, attribute) => {
      return moment().isAfter(value);
    });

    Validator.register('disallowPast', (value, requirement, attribute) => {
      return moment(value).isSameOrAfter(moment(), 'day');
    });

    Validator.register('disallowAfterNineMonths', (value, requirement, attribute) => {
      return moment(value).isSameOrBefore(moment().add(9, 'months'), 'day');
    });

    Validator.register('disallowAfterOneYear', (value, requirement, attribute) => {
      return moment(value).isSameOrBefore(moment().add(1, 'year'), 'day');
    });

    Validator.register('minAge', (value, requirement, attribute) => {
      if (requirement === undefined) {
        console.warn(`you forgot to add minAge:<number> to your validation rule, default is 18`);
        return true;
      }

      const ageValue = moment().diff(moment(value, 'YYYY-MM-DD'), 'years');
      const minAge = Number(requirement);
      return ageValue >= minAge;
    });

    Validator.register('maxAge', (value, requirement, attribute) => {
      if (requirement === undefined) {
        console.warn(`you forgot to add "maxAge:<number>" to your validation rule, default is 118`);
        return true;
      }

      const limitAge = moment().subtract(requirement, 'years');

      return limitAge.isBefore(value);
    });

    /**
     * @description Validates if a date falls within a specific age range restriction
     *   that is passed as comma-separated string of min and max age (e.g. "18,60")
     */
    Validator.register('ageRestriction', (value, requirement) => {
      const inputDate = moment(value, 'YYYY-MM-DD');
      const [minAge, maxAge] = requirement.split(',').map(Number);
      const age = moment().diff(inputDate, 'years', true); // true for floating point years

      return age >= minAge && age <= maxAge;
    });

    Validator.register('includes', (value = '', requirement, attribute) => {
      const includeArr = requirement.split(`,`);
      return Boolean(includeArr.find((string) => value.includes(string)));
    });

    Validator.register('includesNotCaseSensitive', (value, requirement, attribute) => {
      const includeArr = requirement.split(`,`);
      return Boolean(includeArr.find((string) => value.toLowerCase().includes(string.toLowerCase())));
    });

    Validator.register('date', (value, requirement, attribute) => {
      return moment(value, 'YYYY-MM-DD').isValid();
    });

    Validator.register('month', (value, requirement, attribute) => {
      return moment(value, 'YYYY-MM').isValid();
    });

    Validator.register('equal', (value, requirement, attribute) => {
      const toName = requirement;
      if (!toName) return true;

      const fromNameValue = value;
      const toNameValue = actions.case.getCaseValue(toName);
      set(this.state.userData, `${toName}.validatingStatus`, 'valid');
      return isEqual(fromNameValue, toNameValue);
    });

    /**
     * @description Same as `equal`, but compares strings ignoring letter case and surrounding
     *   whitespace. Used for email confirmation (GRN-6990/GRN-7021, registered by GRN-7278) as a
     *   separate rule on purpose: `equal` also backs password confirmation, which must stay
     *   case-sensitive. Trimming keeps this aligned with the server-side check in service-cases
     *   (`EmailConfirmationValidator.sameEmail` trims both sides), so the FE does not reject a
     *   confirmation the BE would accept. Non-string values fall back to the same strict
     *   comparison `equal` uses.
     */
    Validator.register('equalIgnoreCase', (value, requirement, attribute) => {
      const toName = requirement;
      if (!toName) return true;

      const fromNameValue = value;
      const toNameValue = actions.case.getCaseValue(toName);
      set(this.state.userData, `${toName}.validatingStatus`, 'valid');

      if (typeof fromNameValue === 'string' && typeof toNameValue === 'string') {
        return fromNameValue.trim().toLowerCase() === toNameValue.trim().toLowerCase();
      }

      return isEqual(fromNameValue, toNameValue);
    });

    // Using RFC 2822
    Validator.register('email', (value, requirement, attribute) => {
      const mailFormat =
        /^(?!.*\s)(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])$/i;
      return value.match(mailFormat);
    });

    Validator.register('noDuplicationWith', (value, fieldNameToCheck) => {
      if (value !== '') {
        const addressToCheck = actions.case.getCaseValue(fieldNameToCheck);
        return !isEqual(value, addressToCheck);
      }
    });

    /**
     * @description Validates that a date is in the past
     */
    Validator.register('pastMonthAndYear', (value) => {
      const momentObj = moment(value, 'YYYY-MM');
      if (momentObj.isValid) {
        return momentObj.isBefore(moment());
      }
    });

    /**
     * @description Validates that past addresses are in backwards order -> recent address comes first.
     *  Validation rule is added to the root of each previous address' config.
     */
    Validator.register('backwardsOrderDates', (value, requirements, fieldName) => {
      const date = moment(value, 'YYYY-MM-DD');

      if (date.isAfter(moment())) {
        return false;
      }
      const fieldNameArray = fieldName.split('.');
      const key = Number(fieldNameArray[fieldNameArray.length - 1]);
      const currentMonth = actions.case.getCaseValue(`${INTERNAL_CURRENT_DATE}.month`);
      const currentYear = actions.case.getCaseValue(`${INTERNAL_CURRENT_DATE}.year`);
      if (key === 0) {
        return date.isSameOrBefore(moment({ year: currentYear, month: currentMonth }));
      }
      const monthToCheck =
        key === 1 ? currentMonth : actions.case.getCaseValue(`${INTERNAL_PAST_DATES}.${key - 2}.month`);
      const previousPastMonth = actions.case.getCaseValue(`${INTERNAL_PAST_DATES}.${key - 1}.month`);
      const previousPastYear = actions.case.getCaseValue(`${INTERNAL_PAST_DATES}.${key - 1}.year`);
      const previousPastDateMomentObj = moment({ year: previousPastYear, month: previousPastMonth });

      return previousPastMonth === monthToCheck
        ? date.isBefore(previousPastDateMomentObj)
        : date.isSameOrBefore(previousPastDateMomentObj);
    });

    Validator.registerAsync('valid_vrn', async function (value, requirement, casePath, passes) {
      try {
        const oldValue = actions.case.getCaseValue(casePath);

        if (value === oldValue) {
          const oldValidationStatus = actions.caseValidation.getValidationStatus('custom.vrn');

          return passes(oldValidationStatus !== 'invalid');
        }

        const response = await sdkActions.vat.validateVrn(value);

        return passes(response.exist);
      } catch (e) {
        return passes(false);
      }
    });

    /**
     * @description Validates if a date is before (<) a specified date..
     */
    Validator.register('beforeDate', (value, requirement) => {
      const inputDate = moment(value, 'YYYY-MM-DD');
      const maxDate = moment(requirement, 'YYYY-MM-DD');

      return inputDate.isValid() && maxDate.isValid() && inputDate.isBefore(maxDate);
    });

    /**
     * @description Validates if a date is after (>) a specified date..
     */
    Validator.register('afterDate', (value, requirement) => {
      const inputDate = moment(value, 'YYYY-MM-DD');
      const minDate = moment(requirement, 'YYYY-MM-DD');

      return inputDate.isValid() && minDate.isValid() && inputDate.isAfter(minDate);
    });

    /**
     * @description Validates if a date is before or same as (<=) a specified date..
     */
    Validator.register('beforeOrEqualDate', (value, requirement) => {
      const inputDate = moment(value, 'YYYY-MM-DD');
      const maxDate = moment(requirement, 'YYYY-MM-DD');

      return inputDate.isValid() && maxDate.isValid() && inputDate.isSameOrBefore(maxDate);
    });

    /**
     * @description Validates if a date is after or same as (>=) a specified date..
     */
    Validator.register('afterOrEqualDate', (value, requirement) => {
      const inputDate = moment(value, 'YYYY-MM-DD');
      const minDate = moment(requirement, 'YYYY-MM-DD');

      return inputDate.isValid() && minDate.isValid() && inputDate.isSameOrAfter(minDate);
    });

    /**
     * @description Validates if a date is a business day (according to locals.businessDays)
     */
    Validator.register('businessDay', (value) => {
      const businessDays = actions.settings.getUserConfigProp('locals.businessDays');
      const inputDate = moment(value, 'YYYY-MM-DD');

      return inputDate.isValid() && businessDays.includes(inputDate.day());
    });

    Validator.register('israelID', (value) => {
      let sum = 0;
      for (let i = 0; i < value.length; i++) {
        const num = Number(value[i]) * ((i % 2) + 1);
        sum += num > 9 ? num - 9 : num;
      }
      return sum % 10 === 0;
    });

    Validator.register(
      'doesntContainConsecutiveNumbers',
      (value) => {
        const SPECIAL_CHARACTERS_REGEX = /(?:(?:0(?=1)|1(?=2)|2(?=3)|3(?=4)|4(?=5)|5(?=6)|6(?=7)|7(?=8)|8(?=9)){2,}\d)/;
        return !value.match(SPECIAL_CHARACTERS_REGEX);
      },
      undefined,
    );

    Validator.register('greaterThan', (value, valueToCompare) => {
      return parseInt(value) > parseInt(valueToCompare);
    });

    Validator.register('greaterOrEqualThan', (value, valueToCompare) => {
      return parseInt(value) >= parseInt(valueToCompare);
    });

    Validator.register('lowerThan', (value, valueToCompare) => {
      return parseInt(value) < parseInt(valueToCompare);
    });

    Validator.register('lowerOrEqualThan', (value, valueToCompare) => {
      return parseFloat(value) <= parseFloat(valueToCompare);
    });

    /**
     * @description Validates that value of an input already exist under certain case data collection
     *  Using that validation rule we need to specify path to the case data and key of an object item to compare with chosen value
     *  example: itemSelectedTwice:custom.incomeSources.incomes,incomeType
     */
    Validator.register('itemSelectedTwice', (value, params) => {
      const [caseDataToCompare, compareKey] = params.split(',');

      return !actions.case.getCaseValue(caseDataToCompare).some((item) => item[compareKey] === value);
    });

    /**
     * @description Validates if the date has a month offset of at least 1 month from the define date.
     */
    Validator.register('hasMonthOffset', (value, defineDate) => {
      const from = moment(defineDate).startOf('day');
      const to = moment(value).startOf('day');
      const hasMonthOffset = to.diff(from, 'months');
      return hasMonthOffset >= 1;
    });
  },
  /**
   * getErrorCount
   *
   * @description Returns the number of errors for the widget
   * @param {String} name The widget name
   * @returns {Number} The number of errors for the widget
   */
  getErrorCount(name) {
    const count = get(this.state.userData, `${name}.errorCount`, 0);

    return count;
  },
  /**
   * getValidationRules
   *
   * @description Returns the validation rules for the widget
   * @param {String} name The widget name
   * @returns {String[]} The validation rules for the widget
   */
  getValidationRules(name) {
    const { actions } = this.state;
    const rules = actions.settings.getUserConfigProp(`widgetsConfig.${name}.validationRules`) ?? [];

    if (typeof rules === 'string') {
      return rules.split('|');
    }

    return rules;
  },

  getValidity(name) {
    const { actions } = this.state;

    const validatingStatus = actions.caseValidation.getValidationStatus(name);
    const errorMessage = actions.caseValidation.getErrorMessage(name);
    const errorCount = actions.caseValidation.getErrorCount(name);
    const rules = actions.caseValidation.getValidationRules(name);

    return {
      validatingStatus,
      errorMessage,
      errorCount,
      rules,
    };
  },

  /**
   * get validatingStatus from case by name
   * @param {string | Array} names - widget's name
   * @returns {string} 'valid'|'invalid'|'neutral'
   */
  getValidationStatus(names) {
    if (typeof names === `string`) {
      return this.state.actions.case.getCaseProp(names).validatingStatus || 'neutral';
    } else if (Array.isArray(names)) {
      if (
        names.filter((_) => _).some((name) => this.state.actions.case.getCaseProp(name).validatingStatus === `invalid`)
      )
        return `invalid`;
      if (
        names.filter((_) => _).every((name) => this.state.actions.case.getCaseProp(name).validatingStatus === `valid`)
      )
        return `valid`;
      return `neutral`;
    }
    return `neutral`;
  },

  /**
   * get errorMessage from case by name
   * @param {string | Array} names - widget's name
   * @returns {string}
   */
  getErrorMessage(names) {
    if (typeof names === `string`) {
      return get(this.state.userData, `${names}.errorMessage`, '');
    } else {
      const firstErrorMessageName = names
        .filter((_) => _)
        .find((name) => get(this.state.userData, `${name}.errorMessage`));

      return get(this.state.userData, `${firstErrorMessageName}.errorMessage`, '');
    }
  },

  /**
   * get widgetsConfig's validationRules by name, and check if 'required' or 'accepted' is one of them
   * @param {string} name - widget's name
   * @returns {boolean}
   */
  isRequired(name) {
    const validationRules = get(this.state.config.widgetsConfig, `${name}.validationRules`, '');
    return validationRules.includes('required') || validationRules.includes('accepted');
  },

  /**
   * @param {PropertyPath} name
   * @param {{errorMessage: string, validatingStatus: 'valid'|'invalid'|'neutral'}} validationStatus
   */
  setValidationStatus(name, validationStatus) {
    this.setState((state) => {
      const clonedUserData = cloneDeep(state.userData);
      set(clonedUserData, name, {
        ...get(state.userData, name),
        ...validationStatus,
      });

      return { ...state, userData: clonedUserData };
    });
  },

  validate(name, value, configBy) {
    const { actions } = this.state;
    const configByNamespace = `${name}${configBy ? `.${configBy}` : ``}`;

    const validationRules = actions.settings.getUserConfigProp(`widgetsConfig.${configByNamespace}.validationRules`);
    const customErrorMessage = actions.settings.getUserConfigProp(
      `widgetsConfig.${configByNamespace}.customErrorMessage`,
    );

    if (validationRules) {
      const input = { [configByNamespace]: value };

      //add custom xss validation rule and custom error message
      let rules = '';
      if (validationRules === 'noValidation') {
        rules = { [configByNamespace]: 'xss' };
      } else {
        if (Array.isArray(validationRules)) {
          rules = { [configByNamespace]: [...validationRules, 'xss'] };
        } else {
          rules = { [configByNamespace]: validationRules + '|xss' };
        }
      }

      const label = i18n.exists(`${configByNamespace}.label`) ? t(`${configByNamespace}.label`) : undefined;
      const fieldName = i18n.exists(`${configByNamespace}.fieldName`) ? t(`${configByNamespace}.fieldName`) : undefined;

      const customErrorMessages = { ...cloneDeep(customErrorMessage), ...{ xss: 'INPUT' } };
      for (let error in customErrorMessages) {
        customErrorMessages[error] = t(`clientMessages.${customErrorMessages[error]}`, {
          fieldName: fieldName ?? label,
        });
      }

      const validation = new Validator(input, rules, customErrorMessages);

      if (validation.hasAsync) {
        validation.fails(() => {
          actions.caseValidation.setValidationStatus(name, {
            errorMessage: validation.errors.first(configByNamespace),
            errorCount: validation.errorCount,
            validatingStatus: 'invalid',
          });
        });
      } else if (validation.fails()) {
        const errorMessage = validation.errors.first(configByNamespace);

        return {
          errorMessage,
          validatingStatus: 'invalid',
          errorCount: validation.errorCount,
        };
      }

      return {
        errorMessage: '',
        errorCount: 0,
        validatingStatus: validationRules === 'noValidation' ? '' : 'valid',
      };
    }

    return {
      errorMessage: '',
      validatingStatus: 'neutral',
    };
  },

  /**
   * Validate a group of fields
   * @param {String} name config path
   * @return {Boolean} is group valid
   */
  validateGroup(name) {
    const { actions } = this.state;
    const groupObj = actions.case.getCaseProp(name);

    for (let field in groupObj) {
      let fieldObject = groupObj[field];
      let isRequired = actions.caseValidation.isRequired(`${name}.${field}`);
      let isInvalid = fieldObject.validatingStatus === 'invalid' || (isRequired && !fieldObject.value);

      if (typeof fieldObject == 'object' && !fieldObject.validatingStatus)
        isInvalid = !actions.caseValidation.validateGroup(name + '.' + field);

      if (isInvalid) return false;
    }

    return true;
  },
  isValidNames(names) {
    const { actions } = this.state;
    return names.every((name) => actions.caseValidation.getValidationStatus(name) === 'valid');
  },
  checkMultiValidationState(names, statuses) {
    const { actions } = this.state;
    return names.every((name) => statuses.includes(actions.caseValidation.getValidationStatus(name)));
  },
  validateUserData() {
    const { actions } = this.state;
    const invalidProps = {};
    const caseMap = actions.settings.getUserConfigProp('caseMap');
    const flatMapServerCaseToUserData = flatten(caseMap);

    // Validate password field if on step
    const updateUserFields = actions.settings.getUserConfigProp('updateUserFields');
    flatMapServerCaseToUserData[updateUserFields.pw] = updateUserFields.pw;

    for (let path in flatMapServerCaseToUserData) {
      const userDataPath = flatMapServerCaseToUserData[path];
      const prop = actions.case.getCaseProp(userDataPath);
      const value = actions.case.getCaseValue(userDataPath);

      const configByNamespace = `${userDataPath}${prop.configBy ? `.${prop.configBy}` : ``}`;
      // console.log({userDataPath, prop, value, configByNamespace})
      const validation = actions.caseValidation.validate(configByNamespace, value);

      if (validation.validatingStatus === 'invalid' && prop.step === actions.userState.getCurrentStep()) {
        invalidProps[userDataPath] = validation;
      }
    }

    if (Object.keys(invalidProps).length) {
      console.info('These fields are invalid: ', invalidProps);
      return invalidProps;
    } else {
      return false;
    }
  },

  validateCase(validateCaseForCurrentStep = true, validateUserFields = true, disableScrolling = false) {
    const { actions } = this.state;
    const invalidProps = {};
    const caseMap = actions.settings.getUserConfigProp('caseMap') || {};
    const flatMapServerCaseToUserData = flatten(caseMap);

    // Validate password field if on step
    if (validateUserFields) {
      const updateUserFields = actions.settings.getUserConfigProp('updateUserFields');

      flatMapServerCaseToUserData[updateUserFields.pw] = updateUserFields.pw;
    }

    for (let path in flatMapServerCaseToUserData) {
      const userDataPath = flatMapServerCaseToUserData[path];
      const prop = actions.case.getCaseProp(userDataPath);
      const value = actions.case.getCaseValue(userDataPath);

      const configByNamespace = `${userDataPath}${prop.configBy ? `.${prop.configBy}` : ``}`;
      const validation = actions.caseValidation.validate(configByNamespace, value);

      if (validation.validatingStatus === 'invalid') {
        if (validateCaseForCurrentStep && prop.step === actions.userState.getCurrentStep()) {
          invalidProps[userDataPath] = validation;
        } else if (!validateCaseForCurrentStep) {
          invalidProps[userDataPath] = validation;
        }
      }
    }

    if (Object.keys(invalidProps).length) {
      console.info('These fields are invalid: ', invalidProps);
      const propsY = [];
      const clonedUserData = cloneDeep(this.state.userData);
      for (let propName in invalidProps) {
        const validation = invalidProps[propName];
        const elementY = actions.view.getElementY(propName);

        elementY && propsY.push({ propName, elementY });
        set(clonedUserData, propName, {
          ...get(clonedUserData, propName),
          ...validation,
        });
      }

      this.setState((state) => {
        return { ...state, userData: clonedUserData };
      });

      actions.view.focusWidget(invalidProps);

      return false;
    } else {
      return true;
    }
  },
  /**
   * validateSpecificFields
   *
   * @description Validating specific fields that needs to be validated not via continue button
   * @param {Array} fields Array of fields that should be validated
   * @return {Boolean} were fields validated: true/false
   */
  validateSpecificFields(fields) {
    const { actions } = this.state;
    const invalidProps = {};

    fields.forEach((field) => {
      const prop = actions.case.getCaseProp(field);
      const value = actions.case.getCaseValue(field);

      const configByNamespace = `${field}${prop.configBy ? `.${prop.configBy}` : ``}`;
      const validation = actions.caseValidation.validate(configByNamespace, value);

      if (validation.validatingStatus === 'invalid') {
        invalidProps[field] = validation;
      }
    });

    if (Object.keys(invalidProps).length) {
      console.info('These fields are invalid: ', invalidProps);
      const propsY = [];
      const clonedUserData = cloneDeep(this.state.userData);
      for (let propName in invalidProps) {
        const validation = invalidProps[propName];
        const elementY = actions.view.getElementY(propName);

        elementY && propsY.push({ propName, elementY });
        set(clonedUserData, propName, {
          ...get(clonedUserData, propName),
          ...validation,
        });
      }

      this.setState((state) => {
        return { ...state, userData: clonedUserData };
      });

      actions.view.focusWidget(invalidProps);

      return false;
    } else {
      return true;
    }
  },
  /**
   * removeValidationRule
   *
   * @description Remove a rule from `widgetsConfig[`name`].validationRules`
   * @param {String} name The name of the widget
   * @param {String} ruleName The name of the validationRule
   */
  removeValidationRule(name, ruleName) {
    const { actions } = this.state;

    const rulesString = actions.settings.getUserConfigProp(`widgetsConfig.${name}.validationRules`);
    const rulesObj = CaseValidationActions.getValidationRulesObject(rulesString);

    delete rulesObj[ruleName];
    const editedRuleString = CaseValidationActions.getValidationRulesString(rulesObj);
    actions.settings.setUserConfigProp(`widgetsConfig.${name}.validationRules`, editedRuleString);
  },
  /**
   * setValidationRule
   *
   * @description Set a rule to `widgetsConfig[`name`].validationRules`
   * @param {String} name The name of the widget
   * @param {String} ruleName The name of the validationRule
   */
  setValidationRule(name, ruleName, ruleValue) {
    const { actions } = this.state;

    const rulesString = actions.settings.getUserConfigProp(`widgetsConfig.${name}.validationRules`);
    const rulesObj = CaseValidationActions.getValidationRulesObject(rulesString);
    rulesObj[ruleName] = ruleValue;
    const editedRuleString = CaseValidationActions.getValidationRulesString(rulesObj);

    actions.settings.setUserConfigProp(`widgetsConfig.${name}.validationRules`, editedRuleString);
  },

  getValidationRulesObject(rulesString) {
    const validationRulesObject = {};
    rulesString.split('|').forEach((param) => {
      const splittedParam = param.split(':');
      validationRulesObject[splittedParam[0]] = splittedParam[1];
    });

    return validationRulesObject;
  },

  getValidationRulesString(rulesObject) {
    const rulesArray = [];

    for (let rule in rulesObject) {
      if (!rule) continue;

      const value = rulesObject[rule];

      if (value) {
        rulesArray.push(`${rule}:${value}`);
      } else {
        rulesArray.push(rule);
      }
    }

    return rulesArray.join(`|`);
  },

  modifyRulesString(rulesString, rule, value) {
    const validationRulesObject = this.state.actions.caseValidation.getValidationRulesObject(rulesString);
    validationRulesObject[rule] = value;
    return this.state.actions.caseValidation.getValidationRulesString(validationRulesObject);
  },

  getValidationRuleFromString(rulesString, rule) {
    const validationRulesObject = this.state.actions.caseValidation.getValidationRulesObject(rulesString);
    return validationRulesObject[rule];
  },
};
